/**
 * Session context.
 *
 * Holds the current user, and -- when an admin is acting as this captain --
 * both identities. The impersonation banner in App.tsx renders off `actor_id`
 * being non-null, and that value comes straight from the server's `/auth/me`
 * rather than from anything the client decided. A client that could turn its
 * own banner off would be a client that could hide impersonation.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { api, clearSession, getRefreshToken, onSessionLost, setTokens } from './api'
import type { Me, TokenResponse } from './types'

interface AuthValue {
  user: Me | null
  loading: boolean
  signIn: (email: string, password: string, totpCode?: string) => Promise<void>
  signOut: () => Promise<void>
  /** Re-read /auth/me, e.g. after an admin reassigns this captain's vessel. */
  reload: () => Promise<void>
  applySession: (token: TokenResponse) => void
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)

  const reload = useCallback(async () => {
    try {
      setUser(await api.get<Me>('/api/auth/me'))
    } catch {
      setUser(null)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      // A cold start has a refresh token but no access token. Exchange it
      // before asking who we are, or the first paint is always a login screen.
      if (!api.base) return
      if (getRefreshToken()) await api.refresh()
      if (!cancelled) {
        await reload()
        setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [reload])

  useEffect(() => onSessionLost(() => setUser(null)), [])

  const applySession = useCallback((token: TokenResponse) => {
    setTokens(token.access_token, token.refresh_token)
    setUser(token.user)
  }, [])

  const signIn = useCallback(
    async (email: string, password: string, totpCode?: string) => {
      const token = await api.post<TokenResponse>('/api/auth/login', {
        email,
        password,
        totp_code: totpCode || null,
        client: 'captain',
      })
      applySession(token)
    },
    [applySession],
  )

  const signOut = useCallback(async () => {
    const refresh = getRefreshToken()
    try {
      await api.post('/api/auth/logout', { refresh_token: refresh })
    } catch {
      // Signing out must always succeed locally. If the server never heard
      // about it, the token expires on its own schedule.
    }
    clearSession()
    setUser(null)
  }, [])

  const value = useMemo(
    () => ({ user, loading, signIn, signOut, reload, applySession }),
    [user, loading, signIn, signOut, reload, applySession],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}

/** True when an ABG admin is currently acting as this captain. */
export function isImpersonated(user: Me | null): boolean {
  return Boolean(user?.actor_id)
}
